import { createLogger } from "../lib/logger";
import type {
  BrowserContextSummary,
  ContextBrowser,
  CommitProfileResponse,
  CommitProfileResult,
  CreateBrowserContextInput,
  ProfileCandidateResolution,
  ProfileRevisionConflict,
  RenameBrowserContextInput,
  ResolveProfileCandidateResponse,
} from "../types/context";
import {
  isBrowserContextSummary,
  isBrowserContextSummaryList,
} from "../types/context";
import type { SessionSummary } from "../types/session";
import { isSessionsListResponse } from "../types/session";
import { apiFetch, verboseCheck } from "./api";

const slog = createLogger("contexts");

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isUuid(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(
      value,
    )
  );
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isCommitProfileResponse(value: unknown): value is CommitProfileResponse {
  if (!isRecord(value)) return false;
  return (
    typeof value.session_id === "string" &&
    isUuid(value.context_id) &&
    isNonNegativeInteger(value.revision) &&
    typeof value.checksum === "string" &&
    isNonNegativeInteger(value.size_bytes) &&
    typeof value.idempotent === "boolean" &&
    !("owner_id" in value) &&
    !("session_token" in value)
  );
}

function isProfileRevisionConflict(value: unknown): value is ProfileRevisionConflict {
  if (!isRecord(value)) return false;
  return (
    value.code === "profile_revision_conflict" &&
    isUuid(value.candidate_id) &&
    isNonNegativeInteger(value.base_revision) &&
    isNonNegativeInteger(value.current_revision) &&
    !("owner_id" in value) &&
    !("session_token" in value)
  );
}

function isResolveProfileCandidateResponse(
  value: unknown,
): value is ResolveProfileCandidateResponse {
  if (!isRecord(value)) return false;
  return (
    isUuid(value.candidate_id) &&
    (value.action === "keep_canonical" ||
      value.action === "promote_candidate" ||
      value.action === "copy_to_new_context") &&
    isUuid(value.context_id) &&
    (value.revision === null || isNonNegativeInteger(value.revision)) &&
    !("owner_id" in value) &&
    !("session_token" in value)
  );
}

async function responseJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

async function parseContextResponse(
  response: Response,
  operation: string,
): Promise<BrowserContextSummary> {
  await verboseCheck(response, operation);
  const value: unknown = await response.json();
  if (!isBrowserContextSummary(value)) {
    slog.error("invalid public context response", { operation });
    throw new Error(`${operation}: invalid response format`);
  }
  return value;
}

export async function listContexts(): Promise<BrowserContextSummary[]> {
  const response = await verboseCheck(
    await apiFetch("/contexts"),
    "list contexts",
  );
  const value: unknown = await response.json();
  if (!isBrowserContextSummaryList(value)) {
    slog.error("invalid public context list response");
    throw new Error("list contexts: invalid response format");
  }
  return value;
}

export async function getContext(
  contextId: string,
): Promise<BrowserContextSummary> {
  return parseContextResponse(
    await apiFetch(`/contexts/${encodeURIComponent(contextId)}`),
    "get context",
  );
}

export async function createContext(
  name: string,
  browser?: ContextBrowser,
): Promise<BrowserContextSummary> {
  const input: CreateBrowserContextInput = { name: name.trim(), ...(browser ? { browser } : {}) };
  return parseContextResponse(
    await apiFetch("/contexts", {
      method: "POST",
      body: JSON.stringify(input),
    }),
    "create context",
  );
}

export async function retryContextVault(contextId: string): Promise<BrowserContextSummary> {
  return parseContextResponse(await apiFetch(`/contexts/${encodeURIComponent(contextId)}/vault/retry`, {method: "POST"}), "retry context vault");
}

export async function renameContext(
  contextId: string,
  name: string,
): Promise<BrowserContextSummary> {
  const input: RenameBrowserContextInput = { name: name.trim() };
  return parseContextResponse(
    await apiFetch(`/contexts/${encodeURIComponent(contextId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
    "rename context",
  );
}

export interface ContextDeletionBlocker {
  session_id: string;
  state: string;
  retention: string;
  checkpoint: string | null;
}

type ContextDeletionCode = "context_retained_vault" | "context_in_use";

export class ContextDeletionRefusal extends Error {
  readonly code: ContextDeletionCode;
  readonly blockingSessions: ContextDeletionBlocker[];

  constructor(code: ContextDeletionCode, blockingSessions: ContextDeletionBlocker[]) {
    super("La suppression du contexte est refusée ; les données sont conservées.");
    this.name = "ContextDeletionRefusal";
    this.code = code;
    this.blockingSessions = blockingSessions;
  }
}

function isContextDeletionBlocker(value: unknown): value is ContextDeletionBlocker {
  return isRecord(value) && typeof value.session_id === "string" && value.session_id.length > 0 &&
    typeof value.state === "string" && typeof value.retention === "string" &&
    (value.checkpoint === null || typeof value.checkpoint === "string");
}

export async function deleteContext(contextId: string): Promise<void> {
  const response = await apiFetch(`/contexts/${encodeURIComponent(contextId)}`, {
    method: "DELETE",
  });
  if (response.status === 204) return;
  const body = await responseJson(response);
  if (response.status === 409 && isRecord(body)) {
    if (body.code === "context_retained_vault") {
      throw new ContextDeletionRefusal(body.code, []);
    }
    if (body.code === "context_in_use" &&
        Array.isArray(body.blocking_sessions) && body.blocking_sessions.length > 0 &&
        body.blocking_sessions.every(isContextDeletionBlocker)) {
      throw new ContextDeletionRefusal(body.code, body.blocking_sessions);
    }
  }
  // Never turn server prose into UI text or infer that a lost response is safe
  // to replay. Observing a still-present row does not settle the prior request.
  throw new Error("Suppression non confirmée. Actualisez pour observer l’état des contextes, sans répéter la demande.");
}

export async function listContextSessions(
  contextId: string,
): Promise<SessionSummary[]> {
  const response = await verboseCheck(
    await apiFetch(`/contexts/${encodeURIComponent(contextId)}/sessions`),
    "list context sessions",
  );
  const value: unknown = await response.json();
  if (!isSessionsListResponse(value)) {
    slog.error("invalid public session list response");
    throw new Error("list context sessions: invalid response format");
  }
  if (
    value.sessions.some(
      (session) =>
        session.context_mode !== "persistent" ||
        session.context_id !== contextId,
    )
  ) {
    slog.error("mixed context sessions response", { contextId });
    throw new Error("list context sessions: mixed context response");
  }
  return value.sessions;
}

export async function createSessionInContext(
  contextId: string,
  app: string,
): Promise<Response> {
  return apiFetch("/sessions", {
    method: "POST",
    body: JSON.stringify({ context_id: contextId, browser: app, app }),
  });
}

export async function commitSessionProfile(
  sessionId: string,
  contextId: string,
  idempotencyKey: string,
): Promise<CommitProfileResult> {
  const response = await apiFetch(
    `/sessions/${encodeURIComponent(sessionId)}/profile/commit`,
    {
      method: "POST",
      body: JSON.stringify({ idempotency_key: idempotencyKey }),
    },
  );
  const value = await responseJson(response);
  if (response.status === 409 && isProfileRevisionConflict(value)) {
    if (value.candidate_id !== idempotencyKey) {
      throw new Error("Sauvegarde du profil : conflit incohérent.");
    }
    return {
      kind: "conflict",
      conflict: {
        candidate_id: value.candidate_id,
        base_revision: value.base_revision,
        current_revision: value.current_revision,
      },
    };
  }
  if (!response.ok) {
    throw new Error(`Sauvegarde du profil impossible (HTTP ${response.status}).`);
  }
  if (!isCommitProfileResponse(value)) {
    throw new Error("Sauvegarde du profil : réponse invalide.");
  }
  if (value.session_id !== sessionId || value.context_id !== contextId) {
    throw new Error("Sauvegarde du profil : réponse incohérente.");
  }
  return { kind: "committed", value };
}

export async function resolveProfileCandidate(
  contextId: string,
  candidateId: string,
  resolution: ProfileCandidateResolution,
): Promise<ResolveProfileCandidateResponse> {
  const response = await apiFetch(
    `/contexts/${encodeURIComponent(contextId)}/profile/candidates/${encodeURIComponent(candidateId)}/resolve`,
    {
      method: "POST",
      body: JSON.stringify(resolution),
    },
  );
  const value = await responseJson(response);
  if (!response.ok) {
    throw new Error(`Résolution du conflit impossible (HTTP ${response.status}).`);
  }
  if (!isResolveProfileCandidateResponse(value)) {
    throw new Error("Résolution du conflit : réponse invalide.");
  }
  if (
    value.candidate_id !== candidateId ||
    value.action !== resolution.action ||
    (resolution.action !== "copy_to_new_context" && value.context_id !== contextId)
  ) {
    throw new Error("Résolution du conflit : réponse incohérente.");
  }
  return value;
}
